import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Search, Building2, Users, BookOpen, Award, TrendingUp } from 'lucide-react';
import { CollapsibleSection } from '../../components/CollapsibleSection';

const API = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 20;

const PARTNER_TYPE_OPTIONS = [
  { value: '',             label: 'All Partner Types' },
  { value: 'ESI/MPL',     label: 'ESI / MPL' },
  { value: 'MAICPP',      label: 'MAICPP' },
  { value: 'CSP Reseller', label: 'CSP Reseller' },
];

// Badge styles per partner type
const TYPE_BADGE = {
  'ESI/MPL':      { bg: '#dbeafe', color: '#1e40af' },
  'MAICPP':       { bg: '#dcfce7', color: '#166534' },
  'CSP Reseller': { bg: '#fef3c7', color: '#92400e' },
};

// Donut colours per partner type
const DONUT_COLORS = {
  'ESI/MPL':      '#6366f1',
  'MAICPP':       '#22c55e',
  'CSP Reseller': '#f59e0b',
  'Other':        '#94a3b8',
};

const TYPE_LABELS = {
  'ESI/MPL':      'ESI / MPL Partners',
  'MAICPP':       'MAICPP Partners',
  'CSP Reseller': 'CSP Reseller Partners',
};

function PartnerTypeBadge({ value }) {
  if (!value) return <span style={{ color: '#94a3b8' }}>—</span>;
  const style = TYPE_BADGE[value] || { bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600,
      background: style.bg, color: style.color,
    }}>
      {value}
    </span>
  );
}

function StatTile({ label, value, icon: Icon, onClick, active, testId }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      style={{
        background: '#fff',
        border: `1px solid ${active ? '#6366f1' : '#e2e8f0'}`,
        borderRadius: 10,
        padding: '16px 20px',
        minWidth: 130,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: active ? '0 0 0 2px #6366f130' : 'none',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = '#6366f1'; }}
      onMouseLeave={e => { if (!active && onClick) e.currentTarget.style.borderColor = '#e2e8f0'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {Icon && <Icon size={14} color="#6366f1" />}
        <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
        {value != null ? Number(value).toLocaleString() : <span style={{ color: '#e2e8f0' }}>—</span>}
      </div>
    </div>
  );
}

// Custom donut tooltip
function DonutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  return (
    <div style={{
      background: '#1e293b', color: '#fff', borderRadius: 8,
      padding: '8px 12px', fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{name}</div>
      <div>{value.toLocaleString()} partners</div>
    </div>
  );
}

// Custom legend that doubles as filter chips
function DonutLegend({ chartData, activeType, onTypeClick }) {
  const total = chartData.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
      {chartData.map(d => {
        const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0.0';
        const isActive = activeType === d.name;
        return (
          <div
            key={d.name}
            data-testid={`donut-legend-${d.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
            onClick={() => onTypeClick(d.name)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '6px 10px', borderRadius: 7,
              background: isActive ? '#f8fafc' : 'transparent',
              border: `1px solid ${isActive ? '#6366f1' : 'transparent'}`,
              transition: 'all 0.15s',
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#475569', fontWeight: 500, flex: 1 }}>{d.name}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{d.value.toLocaleString()}</span>
            <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 40, textAlign: 'right' }}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function pct(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function fmt(d) {
  if (!d) return '—';
  return String(d).slice(0, 10);
}

export default function ByPartnerReport() {
  const [summary, setSummary]           = useState(null);
  const [rows, setRows]                 = useState([]);
  const [total, setTotal]               = useState(0);
  const [totalPages, setTotalPages]     = useState(1);
  const [page, setPage]                 = useState(1);
  const [loading, setLoading]           = useState(false);
  const [upstreamDown, setUpstreamDown] = useState(false);
  const [typeBreakdown, setTypeBreakdown] = useState(null); // {ESI/MPL: N, MAICPP: N, CSP Reseller: N}

  // Filters
  const [search, setSearch]               = useState('');
  const [partnerTypeFilter, setPartnerTypeFilter] = useState('');
  const [dateType, setDateType]           = useState('enrollment');
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');

  // Fetch breakdown counts (3 parallel requests using per_page=1 to get meta.total)
  const fetchBreakdown = useCallback(async (opts = {}) => {
    const types = ['ESI/MPL', 'MAICPP', 'CSP Reseller'];
    try {
      const results = await Promise.all(
        types.map(type =>
          axios.get(`${API}/api/lup/v2/admin/reports/by-partner`, {
            params: {
              per_page: 1,
              partner_type: type,
              date_type: opts.dateType ?? 'enrollment',
              ...(opts.dateFrom && { date_from: opts.dateFrom }),
              ...(opts.dateTo   && { date_to:   opts.dateTo   }),
            },
            withCredentials: true,
          }).then(r => ({ type, total: r.data?.meta?.total ?? 0 }))
           .catch(() => ({ type, total: 0 }))
        )
      );
      const breakdown = {};
      results.forEach(({ type, total }) => { breakdown[type] = total; });
      setTypeBreakdown(breakdown);
    } catch {
      setTypeBreakdown(null);
    }
  }, []);

  const fetchData = useCallback(async (pg = 1, opts = {}) => {
    setLoading(true);
    setUpstreamDown(false);
    try {
      const params = {
        page: pg,
        per_page: PER_PAGE,
        date_type: opts.dateType ?? dateType,
        ...(opts.search       ?? search)             && { search:       opts.search       ?? search },
        ...(opts.partnerType  ?? partnerTypeFilter)  && { partner_type: opts.partnerType  ?? partnerTypeFilter },
        ...(opts.dateFrom     ?? dateFrom)           && { date_from:    opts.dateFrom     ?? dateFrom },
        ...(opts.dateTo       ?? dateTo)             && { date_to:      opts.dateTo       ?? dateTo },
      };
      const { data } = await axios.get(`${API}/api/lup/v2/admin/reports/by-partner`, {
        params, withCredentials: true,
      });
      setRows(data.data || []);
      setSummary(data.summary || null);
      const tot = data.meta?.total ?? data.total ?? 0;
      const pp  = data.meta?.per_page ?? PER_PAGE;
      setTotal(tot);
      setTotalPages(Math.max(1, Math.ceil(tot / pp)));
    } catch (e) {
      if ([502, 503, 504].includes(e.response?.status)) {
        setUpstreamDown(true);
      }
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [search, partnerTypeFilter, dateType, dateFrom, dateTo]);

  useEffect(() => {
    fetchData(1);
    fetchBreakdown();
  }, []); // eslint-disable-line

  const handleApply = () => {
    setPage(1);
    fetchData(1);
    // Refresh breakdown when date filters change
    fetchBreakdown({ dateType, dateFrom, dateTo });
  };

  const handleTypeFilterFromTile = (type) => {
    const next = partnerTypeFilter === type ? '' : type;
    setPartnerTypeFilter(next);
    setPage(1);
    fetchData(1, { partnerType: next });
  };

  // Build donut chart data from typeBreakdown
  const donutData = typeBreakdown
    ? Object.entries(typeBreakdown)
        .map(([name, value]) => ({ name, value, color: DONUT_COLORS[name] || DONUT_COLORS.Other }))
        .filter(d => d.value > 0)
        .sort((a, b) => b.value - a.value)
    : [];

  return (
    <div style={{ padding: '0 16px' }}>
      {/* Header */}
      <div style={{ padding: '20px 0 16px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>
          By Partner Report
        </h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
          Partner performance across ESI/MPL, MAICPP and CSP Reseller.
          Filter by partner type or date basis.
        </p>
      </div>

      {/* Summary tiles — row 1: totals */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <StatTile
          testId="tile-total-partners"
          label="Partners" value={summary?.total_partners}
          icon={Building2}
        />
        <StatTile
          testId="tile-total-learners"
          label="Learners" value={summary?.total_learners}
          icon={Users}
        />
        <StatTile
          testId="tile-total-enrollments"
          label="Enrollments" value={summary?.total_enrollments}
          icon={BookOpen}
        />
        <StatTile
          testId="tile-total-completions"
          label="Completions" value={summary?.total_completions}
          icon={TrendingUp}
        />
        <StatTile
          testId="tile-total-badges"
          label="Badges" value={summary?.total_badges_awarded}
          icon={Award}
        />
      </div>

      {/* Summary tiles — row 2: by_type (clickable, apply filter) */}
      {summary?.by_type && Object.keys(summary.by_type).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          {Object.entries(summary.by_type).map(([type, count]) => (
            <StatTile
              key={type}
              testId={`summary-tile-${type.toLowerCase().replace(/[^a-z]/g, '-')}`}
              label={TYPE_LABELS[type] ?? type}
              value={count}
              onClick={() => handleTypeFilterFromTile(type)}
              active={partnerTypeFilter === type}
            />
          ))}
        </div>
      )}

      {/* Partner Type Donut Chart — Analytics section — always rendered */}
      <CollapsibleSection title="Analytics" defaultExpanded={true}>
        <div
          data-testid="partner-type-donut-section"
          className="report-card"
          style={{ padding: '20px 24px', marginBottom: 0 }}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Partner Type Distribution</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              Click a segment or legend item to filter the table below
            </div>
          </div>

          {typeBreakdown === null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#94a3b8', fontSize: 13, padding: '16px 0' }}>
              <div className="spinner" style={{ width: 16, height: 16 }} />
              Loading breakdown…
            </div>
          ) : donutData.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13, padding: '16px 0' }}>
              Partner breakdown unavailable (upstream service offline).
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
              {/* Donut with center label overlay */}
              <div style={{ position: 'relative', width: 200, height: 200, flexShrink: 0 }}>
                <div data-testid="partner-type-donut" style={{ width: '100%', height: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        onClick={(entry) => handleTypeFilterFromTile(entry.name)}
                        style={{ cursor: 'pointer' }}
                      >
                        {donutData.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={entry.color}
                            opacity={partnerTypeFilter && partnerTypeFilter !== entry.name ? 0.35 : 1}
                            stroke={partnerTypeFilter === entry.name ? '#1e293b' : 'none'}
                            strokeWidth={partnerTypeFilter === entry.name ? 2 : 0}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<DonutTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Centre label: absolutely centred within the donut wrapper */}
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                    {donutData.reduce((s, d) => s + d.value, 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>
                    Total
                  </div>
                </div>
              </div>

              {/* Legend */}
              <DonutLegend
                chartData={donutData}
                activeType={partnerTypeFilter}
                onTypeClick={handleTypeFilterFromTile}
              />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Data section — collapsed by default */}
      <CollapsibleSection title="Data" defaultExpanded={false}>
        {/* Upstream unavailable notice */}
        {upstreamDown && (
          <div data-testid="upstream-error" style={{
            background: '#fef9ec', border: '1px solid #fde68a',
            borderRadius: 8, padding: '20px 24px', marginBottom: 16,
          }}>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4, fontSize: 14 }}>
              Report Deploying
            </div>
            <div style={{ color: '#78350f', fontSize: 13 }}>
              The By Partner report is being deployed on the upstream service.
              It will be available shortly — no action needed.
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="report-card" style={{ padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>

          {/* Search */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Search</div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={13} style={{ position: 'absolute', left: 9, color: '#94a3b8' }} />
              <input
                data-testid="by-partner-search"
                type="text"
                placeholder="Partner name or POID…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleApply()}
                style={{
                  height: 34, paddingLeft: 28, paddingRight: 10, width: 220,
                  border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13,
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Partner Type dropdown */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Partner Type</div>
            <select
              data-testid="partner-type-filter"
              value={partnerTypeFilter}
              onChange={e => { setPartnerTypeFilter(e.target.value); setPage(1); }}
              style={{
                height: 34, padding: '0 28px 0 10px', border: '1px solid #e2e8f0',
                borderRadius: 7, fontSize: 13, color: '#475569', background: '#fff',
                cursor: 'pointer', outline: 'none', appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
              }}
            >
              {PARTNER_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>From</div>
            <input
              data-testid="date-from-input"
              type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ height: 34, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none' }}
            />
          </div>

          {/* Date To */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>To</div>
            <input
              data-testid="date-to-input"
              type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ height: 34, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none' }}
            />
          </div>

          {/* Date type toggle */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Date Basis</div>
            <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 7, overflow: 'hidden' }}>
              {[
                { value: 'enrollment',  label: 'Enrollment' },
                { value: 'completion',  label: 'Completion' },
              ].map(opt => (
                <button
                  key={opt.value}
                  data-testid={`date-type-${opt.value}`}
                  onClick={() => { setDateType(opt.value); setPage(1); }}
                  style={{
                    height: 34, padding: '0 14px', border: 'none',
                    background: dateType === opt.value ? '#1e293b' : '#fff',
                    color: dateType === opt.value ? '#fff' : '#64748b',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Apply */}
          <button
            data-testid="apply-filters-btn"
            onClick={handleApply}
            style={{
              height: 34, padding: '0 18px', background: '#1e293b', color: '#fff',
              border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700,
              cursor: 'pointer', alignSelf: 'flex-end',
            }}
          >
            Apply
          </button>

          {/* Clear */}
          {(search || partnerTypeFilter || dateFrom || dateTo) && (
            <button
              data-testid="clear-filters-btn"
              onClick={() => {
                setSearch(''); setPartnerTypeFilter('');
                setDateFrom(''); setDateTo('');
                setPage(1);
                fetchData(1, { search: '', partnerType: '', dateFrom: '', dateTo: '' });
              }}
              style={{
                height: 34, padding: '0 14px', background: 'none',
                color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7,
                fontSize: 13, cursor: 'pointer', alignSelf: 'flex-end',
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
            Partner Records
            {total > 0 && <span style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginLeft: 8 }}>({total.toLocaleString()} total)</span>}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 10px' }} />
            <div style={{ color: '#64748b', fontSize: 13 }}>Loading partner data…</div>
          </div>
        ) : !upstreamDown && rows.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            {loading ? '' : 'No partner records found for the current filters.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="by-partner-table">
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {[
                    { label: 'POID / Domain',       align: 'left'  },
                    { label: 'Partner Name',         align: 'left'  },
                    { label: 'Type',                 align: 'left'  },
                    { label: 'Learners',             align: 'right' },
                    { label: 'Enrolments',           align: 'right' },
                    { label: 'Completions',          align: 'right' },
                    { label: 'Course Rate',          align: 'right' },
                    { label: 'Journey Enrolments',   align: 'right' },
                    { label: 'Journey Completions',  align: 'right' },
                    { label: 'Journey Rate',         align: 'right' },
                    { label: 'Badges',               align: 'right' },
                    { label: 'Last Active',          align: 'left'  },
                  ].map(h => (
                    <th key={h.label} style={{
                      padding: '10px 14px', textAlign: h.align,
                      fontSize: 11, fontWeight: 700, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                    }}>{h.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row.partner_one_id || i}
                    data-testid={`partner-row-${i}`}
                    style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  >
                    <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>
                      {row.partner_one_id || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.partner_name || '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <PartnerTypeBadge value={row.partner_type} />
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>
                      {row.learner_count != null ? row.learner_count.toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#475569' }}>
                      {row.course_enrollments != null ? row.course_enrollments.toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#475569' }}>
                      {row.course_completions != null ? row.course_completions.toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#475569' }}>
                      {pct(row.course_completion_rate)}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#475569' }}>
                      {row.journey_enrollments != null ? row.journey_enrollments.toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#475569' }}>
                      {row.journey_completions != null ? row.journey_completions.toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#475569' }}>
                      {pct(row.journey_completion_rate)}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#475569' }}>
                      {row.badges_awarded != null ? row.badges_awarded.toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 12 }}>
                      {fmt(row.last_active_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div data-testid="by-partner-pagination" style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            gap: 8, padding: '12px 16px', borderTop: '1px solid #f1f5f9', fontSize: 13,
          }}>
            <span style={{ color: '#64748b' }}>Page {page} of {totalPages} ({total.toLocaleString()} total)</span>
            <button
              onClick={() => { setPage(p => p - 1); fetchData(page - 1); }}
              disabled={page <= 1}
              style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page > 1 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >‹</button>
            <button
              onClick={() => { setPage(p => p + 1); fetchData(page + 1); }}
              disabled={page >= totalPages}
              style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page < totalPages ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >›</button>
          </div>
        )}
      </div>
      </CollapsibleSection>
    </div>
  );
}
